import React, { useState, useEffect, useRef } from 'react'
import { object, array } from 'prop-types'
import { PrismicRichText } from '@prismicio/react'
import Select from 'react-select'
import { navigate } from 'gatsby'

import FilterRow from './FilterRow'
import { ProductSearchField } from './styles/FilterRow.styles'
import { DROPDOWN_STYLES } from '../util/constants'
// import MediaLibraryItem from './ContentBlocks/MediaLibraryItem'
import * as Styled from './styles/ProductDirectory.styles'
import { RegionContainer } from './styles/Utility.styles'
import SliceRenderer from './ContentBlocks/SliceRenderer'
import ProductGridItem from './ContentBlocks/ProductGridItem'
import Disclaimer from '../components/ContentBlocks/Disclaimer'
import CtaLink from '../components/ContentBlocks/CtaLink'
import { TopLineGoogleAdContainer } from '../components/styles/GoogleAdsCommon.styles'
import AdSlot from '../components/AdSlot'
import PaginationControl from '../components/PaginatonControl'
import LandingPageHero from './Heroes/LandingPageHero'
import WithLocation from '../util/WithLocation'
import SEAL_HERO from '../images/seal-of-recognition.png'

const ITEMS_PER_PAGE = 18

const sortOptions = [
  { value: 'A->Z', label: 'A\u2192Z' },
  { value: 'Z->A', label: 'Z\u2192A' },
  { value: 'Newest->Oldest', label: 'Newest\u2192Oldest' },
  { value: 'Oldest->Newest', label: 'Oldest\u2192Newest' },
]

const getProductTypeOptions = (types, filteredProducts) => {
  const options = []
  const uniqueTypes = new Set()

  filteredProducts.forEach(product => {
    const types =
      product?.node?.data?.product_types?.map(
        type => type.type.document.data.product_type
      ) || []
    types.forEach(type => uniqueTypes.add(type))
  })

  uniqueTypes.forEach(type => {
    options.push({
      value: type,
      label: type,
    })
  })
  return options.sort((a, b) => a.label.localeCompare(b.label))
}

const getProductBrandOptions = (companies, filteredProducts) => {
  const options = []
  const uniqueBrands = new Set()

  filteredProducts.forEach(product => {
    if (product?.node?.data?.product_company?.document) {
      uniqueBrands.add(
        product.node.data.product_company.document.data.product_company
      )
    }
  })

  uniqueBrands.forEach(brand => {
    options.push({
      value: brand,
      label: brand,
    })
  })
  return options.sort((a, b) => a.label.localeCompare(b.label))
}

const getProductCategoryOptions = (categories, filteredProducts) => {
  const options = []
  const uniqueCategories = new Set()

  filteredProducts.forEach(product => {
    const categories =
      product?.node?.data?.product_categories?.map(
        cat => cat.category.document.data.product_category
      ) || []
    categories.forEach(category => uniqueCategories.add(category))
  })

  uniqueCategories.forEach(category => {
    options.push({
      value: category,
      label: category,
    })
  })
  return options.sort((a, b) => a.label.localeCompare(b.label))
}

const ProductItemGrid = ({
  sortOption,
  brandFilter,
  typeFilter,
  categoryFilter,
  searchInput,
  productsList,
  firstItemForPage,
  lastItemForPage,
  setFilteredItemsCount,
}) => {
  const filtered = productsList
    .filter(item => {
      if (searchInput === '') return true
      const name = item.node.data.product_name?.text.toLowerCase()
      const description = item.node.data.product_description?.text.toLowerCase()
      const brand =
        item.node.data.product_company?.document?.data?.product_company?.toLowerCase() ||
        ''
      return (
        brand.includes(searchInput.toLowerCase()) ||
        name.includes(searchInput.toLowerCase()) ||
        description.includes(searchInput.toLowerCase())
      )
    })
    .filter(item => {
      if (!categoryFilter) {
        return true
      }
      const categories =
        item?.node?.data?.product_categories?.map(
          cat => cat.category.document.data.product_category
        ) || []
      return categories.includes(categoryFilter)
    })
    .filter(item => {
      if (!brandFilter) {
        return true
      }
      if (!item?.node?.data?.product_company?.document) {
        return false
      }
      return brandFilter
        ? item?.node?.data?.product_company.document.data.product_company ===
            brandFilter
        : true
    })
    .filter(item => {
      if (!typeFilter) {
        return true
      }
      const types =
        item?.node?.data?.product_types?.map(
          type => type.type.document.data.product_type
        ) || []
      return types.includes(typeFilter)
    })
  const sorted = filtered.sort((a, b) => {
    const aDate = new Date(a.node.first_publication_date)
    const bDate = new Date(b.node.first_publication_date)
    const aName = a.node.data.product_name?.text.toLowerCase() ?? ''
    const bName = b.node.data.product_name?.text.toLowerCase() ?? ''
    if (sortOption.value === 'Newest->Oldest') {
      return aDate > bDate ? -1 : 1
    } else if (sortOption.value === 'Oldest->Newest') {
      return aDate < bDate ? -1 : 1
    } else if (sortOption.value === 'A->Z') {
      return aName < bName ? -1 : 1
    } else if (sortOption.value === 'Z->A') {
      return aName > bName ? -1 : 1
    } else {
      return 0
    }
  })

  useEffect(() => {
    setFilteredItemsCount(filtered.length)
  }, [filtered])

  return (
    <Styled.ItemsGridContainer>
      {sorted.slice(firstItemForPage, lastItemForPage).map(item => (
        <ProductGridItem key={item.node.id} item={item.node.data} />
      ))}
    </Styled.ItemsGridContainer>
  )
}

const ProductDirectory = ({
  doc,
  types,
  categories,
  companies,
  search,
  productsList,
  topLineAdBlock,
  paginationChangeAction,
  location,
}) => {
  const [searchInput, setSearchInput] = useState('')
  const [categoryFilter, setCategoryFilter] = useState(null)
  const [brandFilter, setBrandFilter] = useState(null)
  const [typeFilter, setTypeFilter] = useState(null)

  const [sortOption, setSortOption] = useState(sortOptions[0])
  const [firstItemForPage, setFirstItemForPage] = useState(0)
  const [lastItemForPage, setLastItemForPage] = useState(ITEMS_PER_PAGE)
  const [filteredItemsCount, setFilteredItemsCount] = useState(null)
  const isInitialMount = useRef(true)
  const currentPagePath = useRef(location.pathname)

  // Clear filters when component unmounts
  useEffect(() => {
    return () => {
      // Component cleanup - no session storage to clear
    }
  }, [])

  // Check if we've navigated to a different page (without query params)
  useEffect(() => {
    const currentPath = location.pathname
    const hasQueryParams = Object.keys(search).length > 0

    if (currentPagePath.current !== currentPath && !hasQueryParams) {
      // We've navigated to a different page without query params, clear filters
      setCategoryFilter(null)
      setBrandFilter(null)
      setTypeFilter(null)
      setSearchInput('')
      currentPagePath.current = currentPath
    }
  }, [location.pathname, search])

  // Handle when user navigates back to a page without filter parameters
  useEffect(() => {
    const hasFilterParams =
      search.category || search.brand || search.type || search.search

    if (
      !hasFilterParams &&
      (categoryFilter || brandFilter || typeFilter || searchInput)
    ) {
      // User navigated back to a page without filter params, clear all filters
      setCategoryFilter(null)
      setBrandFilter(null)
      setTypeFilter(null)
      setSearchInput('')
    }
  }, [search, categoryFilter, brandFilter, typeFilter, searchInput])

  // Function to update URL with current filter values
  const updateURL = (
    newCategoryFilter,
    newBrandFilter,
    newTypeFilter,
    newSearchInput
  ) => {
    const currentParams = new URLSearchParams(window.location.search)
    let hasChanges = false

    // Update or remove category parameter
    if (newCategoryFilter) {
      if (currentParams.get('category') !== newCategoryFilter) {
        currentParams.set('category', newCategoryFilter)
        hasChanges = true
      }
    } else {
      if (currentParams.has('category')) {
        currentParams.delete('category')
        hasChanges = true
      }
    }

    // Update or remove brand parameter
    if (newBrandFilter) {
      if (currentParams.get('brand') !== newBrandFilter) {
        currentParams.set('brand', newBrandFilter)
        hasChanges = true
      }
    } else {
      if (currentParams.has('brand')) {
        currentParams.delete('brand')
        hasChanges = true
      }
    }

    // Update or remove type parameter
    if (newTypeFilter) {
      if (currentParams.get('type') !== newTypeFilter) {
        currentParams.set('type', newTypeFilter)
        hasChanges = true
      }
    } else {
      if (currentParams.has('type')) {
        currentParams.delete('type')
        hasChanges = true
      }
    }

    // Update or remove search parameter
    if (newSearchInput) {
      if (currentParams.get('search') !== newSearchInput) {
        currentParams.set('search', newSearchInput)
        hasChanges = true
      }
    } else {
      if (currentParams.has('search')) {
        currentParams.delete('search')
        hasChanges = true
      }
    }

    // Only update URL if there are actual changes
    if (hasChanges) {
      const newSearch = currentParams.toString()
      const searchString = newSearch ? `?${newSearch}` : ''
      navigate(searchString)
    }
  }

  useEffect(() => {
    if (search.category) {
      setCategoryFilter(search.category)
    }
    if (search.brand) {
      setBrandFilter(search.brand)
    }
    if (search.type) {
      setTypeFilter(search.type)
    }
    if (search.search) {
      setSearchInput(search.search)
    }
  }, [search, categories, companies, types])

  // Update URL when filters change
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    updateURL(categoryFilter, brandFilter, typeFilter, searchInput)
  }, [categoryFilter, brandFilter, typeFilter, searchInput])

  // Get filtered products for dependent filter options
  const getFilteredProducts = () => {
    return productsList
      .filter(item => {
        if (searchInput === '') return true
        const name = item.node.data.product_name?.text.toLowerCase()
        const description =
          item.node.data.product_description?.text.toLowerCase()
        const brand =
          item.node.data.product_company?.document?.data?.product_company?.toLowerCase() ||
          ''
        return (
          brand.includes(searchInput.toLowerCase()) ||
          name.includes(searchInput.toLowerCase()) ||
          description.includes(searchInput.toLowerCase())
        )
      })
      .filter(item => {
        if (!categoryFilter) {
          return true
        }
        const categories =
          item?.node?.data?.product_categories?.map(
            cat => cat.category.document.data.product_category
          ) || []
        return categories.includes(categoryFilter)
      })
      .filter(item => {
        if (!brandFilter) {
          return true
        }
        if (!item?.node?.data?.product_company?.document) {
          return false
        }
        return brandFilter
          ? item?.node?.data?.product_company.document.data.product_company ===
              brandFilter
          : true
      })
      .filter(item => {
        if (!typeFilter) {
          return true
        }
        const types =
          item?.node?.data?.product_types?.map(
            type => type.type.document.data.product_type
          ) || []
        return types.includes(typeFilter)
      })
  }

  const filteredProducts = getFilteredProducts()

  if (!doc || !types || !categories || !companies) {
    return null
  }
  const pageTitle = doc.data.title?.text ?? ''
  const bodyText = doc.data?.body_text
  const disclaimer = doc.data?.product_page_disclaimer
  const categoryImage = doc.data?.category_image

  const isCategoryPage = doc.data?.isCategoryPage

  // const mediaOptions = doc.data?.
  return (
    <>
      {isCategoryPage && (
        <>
          <LandingPageHero
            pageTitle={pageTitle}
            heroText={bodyText}
            heroImage={categoryImage}
            heroGraphic="Disease / Treatment"
            uid={doc.uid}
            halfHeight={true}
            includeCta={true}
            ctaLink={doc.data?.cta_link}
            ctaText={doc.data?.cta_link_text}
          />

          {/* <pre>{JSON.stringify(doc.data, null, 2)}</pre> */}
          {doc?.data?.featured_product_block && (
            <SliceRenderer block={doc.data.featured_product_block} />
          )}
        </>
      )}
      {!isCategoryPage && (
        <RegionContainer>
          <Styled.HeroRegion>
            <Styled.TextColumn>
              <Styled.HeroTitle>{pageTitle}</Styled.HeroTitle>
              <Styled.HeroBody as="div">
                {bodyText && <PrismicRichText field={bodyText.richText} />}
                {doc.data?.cta_link && (
                  <CtaLink
                    text={doc.data.cta_link_text}
                    link={doc.data.cta_link}
                  />
                )}
              </Styled.HeroBody>
            </Styled.TextColumn>
            <Styled.ImageColumn>
              <Styled.HeroImage src={SEAL_HERO} alt="Seal of Recognition" />
            </Styled.ImageColumn>
          </Styled.HeroRegion>
        </RegionContainer>
      )}
      {topLineAdBlock && (
        <TopLineGoogleAdContainer>
          <AdSlot divId={`div-${topLineAdBlock}`} />
        </TopLineGoogleAdContainer>
      )}
      <div style={{ marginTop: '2.4rem' }}>
        <FilterRow sectionTitle="Filter Products">
          {!isCategoryPage && (
            <Styled.FilterLabelSelectPair>
              <Styled.FilterLabel>Category</Styled.FilterLabel>
              <Select
                placeholder="Category"
                options={getProductCategoryOptions(
                  categories,
                  filteredProducts
                )}
                isClearable={true}
                styles={DROPDOWN_STYLES}
                width="160px"
                marginRight="10px"
                value={
                  categoryFilter
                    ? { value: categoryFilter, label: categoryFilter }
                    : null
                }
                onChange={e =>
                  e?.value
                    ? setCategoryFilter(e.value)
                    : setCategoryFilter(null)
                }
              />
            </Styled.FilterLabelSelectPair>
          )}
          <Styled.FilterLabelSelectPair>
            <Styled.FilterLabel>Brand</Styled.FilterLabel>
            <Select
              placeholder="Brand"
              options={getProductBrandOptions(companies, filteredProducts)}
              isClearable={true}
              styles={DROPDOWN_STYLES}
              width="160px"
              marginRight="10px"
              value={
                brandFilter ? { value: brandFilter, label: brandFilter } : null
              }
              onChange={e =>
                e?.value ? setBrandFilter(e.value) : setBrandFilter(null)
              }
            />
          </Styled.FilterLabelSelectPair>
          <Styled.FilterLabelSelectPair>
            <Styled.FilterLabel>Type</Styled.FilterLabel>
            <Select
              placeholder="Type"
              options={getProductTypeOptions(types, filteredProducts)}
              isClearable={true}
              styles={DROPDOWN_STYLES}
              width="160px"
              marginRight="10px"
              value={
                typeFilter ? { value: typeFilter, label: typeFilter } : null
              }
              onChange={e =>
                e?.value ? setTypeFilter(e.value) : setTypeFilter(null)
              }
            />
          </Styled.FilterLabelSelectPair>
          <Styled.FilterLabelSelectPair>
            <Styled.FilterLabel>Sort</Styled.FilterLabel>
            <Select
              options={sortOptions}
              value={sortOption}
              defaultValue={sortOptions[0]}
              styles={DROPDOWN_STYLES}
              width="180px"
              marginRight="10px"
              onChange={e => setSortOption({ value: e.value, label: e.label })}
            />
          </Styled.FilterLabelSelectPair>
          <Styled.FilterLabelSelectPair>
            <Styled.FilterLabel>Search</Styled.FilterLabel>
            <ProductSearchField
              placeholder="Search"
              onChange={e => setSearchInput(e.target.value)}
              value={searchInput}
              name="search"
            />
          </Styled.FilterLabelSelectPair>
        </FilterRow>
      </div>

      <Styled.GreyFill>
        <RegionContainer>
          <ProductItemGrid
            sortOption={sortOption}
            brandFilter={brandFilter}
            typeFilter={typeFilter}
            categoryFilter={categoryFilter}
            searchInput={searchInput}
            productsList={productsList}
            firstItemForPage={firstItemForPage}
            lastItemForPage={lastItemForPage}
            setFilteredItemsCount={setFilteredItemsCount}
          />
          <PaginationControl
            itemCount={
              filteredItemsCount ? filteredItemsCount : productsList.length
            }
            itemsPerPage={ITEMS_PER_PAGE}
            setFirstItemForPage={setFirstItemForPage}
            setLastItemForPage={setLastItemForPage}
            paginationChangeAction={paginationChangeAction}
          />
        </RegionContainer>
        {disclaimer && <Disclaimer disclaimer={disclaimer} />}
      </Styled.GreyFill>
    </>
  )
}

ProductDirectory.propTypes = {
  doc: object,
  types: array,
  categories: array,
  companies: array,
  productsList: array,
  location: object,
}

export default WithLocation(ProductDirectory)
